import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Loader2, Plus, Edit, Trash2, Save, ArrowLeft, Eye, EyeOff, Star, BookOpen, Image, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gallery Management Tab
export const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ image_url: '', label: '', category: 'svetlomety', wide: false, order: 0 });

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/gallery`, { withCredentials: true });
      setItems(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => { setForm({ image_url: '', label: '', category: 'svetlomety', wide: false, order: 0 }); setEditingItem(null); setShowForm(false); setUploadProgress(''); };

  const handleEdit = (item) => {
    setForm({ image_url: item.image_url, label: item.label, category: item.category, wide: item.wide || false, order: item.order || 0 });
    setEditingItem(item); setShowForm(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Súbor je príliš veľký. Maximum 10MB.');
      return;
    }
    
    setUploading(true);
    setUploadProgress('Nahrávam...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(p => ({ ...p, image_url: imageUrl }));
      setUploadProgress('Nahrané!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Chyba pri nahrávaní');
      setUploadProgress('');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.image_url || !form.label) return;
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/gallery/${editingItem.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/gallery`, form, { withCredentials: true });
      }
      await fetchItems(); resetForm();
    } catch (e) { alert(e.response?.data?.detail || 'Chyba'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Vymazať fotku?')) return;
    await axios.delete(`${API}/admin/gallery/${id}`, { withCredentials: true });
    await fetchItems();
  };

  const toggleActive = async (item) => {
    await axios.put(`${API}/admin/gallery/${item.id}`, { active: !item.active }, { withCredentials: true });
    await fetchItems();
  };

  if (showForm) {
    return (
      <div data-testid="gallery-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-medium text-white">{editingItem ? 'Upraviť fotku' : 'Nová fotka'}</h2>
          <button onClick={resetForm} className="text-zinc-400 hover:text-white flex items-center gap-2"><ArrowLeft size={18} /> Späť</button>
        </div>
        <div className="space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Nahrať fotku</label>
            <div className="flex gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="gallery-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="gallery-upload-btn"
                className="flex items-center gap-2 bg-[#00BFFF]/10 border border-[#00BFFF]/30 text-[#00BFFF] px-5 py-3 font-medium hover:bg-[#00BFFF]/20 transition-all disabled:opacity-50"
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {uploading ? 'Nahrávam...' : 'Vybrať z počítača / telefónu'}
              </button>
              {uploadProgress === 'Nahrané!' && (
                <span className="flex items-center text-green-400 text-sm font-medium">Nahrané!</span>
              )}
            </div>
          </div>

          {/* URL Input (alternative) */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Alebo zadať URL obrázka</label>
            <input data-testid="gallery-form-url" value={form.image_url} onChange={(e) => setForm(p => ({ ...p, image_url: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF] text-sm" placeholder="https://..." />
          </div>

          {/* Preview */}
          {form.image_url && (
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Náhľad</label>
              <img src={form.image_url} alt="Preview" className="w-64 h-40 object-cover border border-white/10 rounded" />
            </div>
          )}

          <div>
            <label className="block text-sm text-zinc-400 mb-1">Popis</label>
            <input data-testid="gallery-form-label" value={form.label} onChange={(e) => setForm(p => ({ ...p, label: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="Popis fotky" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Kategória</label>
              <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]">
                <option value="svetlomety">Svetlomety</option>
                <option value="interiér">Interiér</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Poradie</label>
              <input type="number" value={form.order} onChange={(e) => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer text-zinc-400">
                <input type="checkbox" checked={form.wide} onChange={(e) => setForm(p => ({ ...p, wide: e.target.checked }))} className="w-4 h-4 accent-[#00BFFF]" />
                Široká fotka (2 stĺpce)
              </label>
            </div>
          </div>
          <button data-testid="gallery-form-save" onClick={handleSave} disabled={saving || !form.image_url || !form.label}
            className="flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-3 font-semibold hover:bg-[#33CCFF] transition-all disabled:opacity-50">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {editingItem ? 'Uložiť' : 'Pridať fotku'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="gallery-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-medium text-white">Správa galérie</h2>
          <p className="text-sm text-zinc-500 mt-1">{items.length} fotiek</p>
        </div>
        <button data-testid="gallery-add-btn" onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-4 py-2.5 font-semibold hover:bg-[#33CCFF] transition-all">
          <Plus size={18} /> Pridať fotku
        </button>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" /></div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(item => (
            <div key={item.id} data-testid={`gallery-admin-${item.id}`} className={`group relative border border-white/10 overflow-hidden ${!item.active ? 'opacity-40' : ''}`}>
              <img src={item.image_url} alt={item.label} className="w-full aspect-video object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                <p className="text-white text-xs font-medium text-center px-2">{item.label}</p>
                <p className="text-[#00BFFF] text-xs">{item.category} | #{item.order}</p>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => toggleActive(item)} className="p-1.5 bg-white/10 hover:bg-white/20 rounded transition-colors">
                    {item.active ? <Eye size={14} className="text-green-400" /> : <EyeOff size={14} className="text-red-400" />}
                  </button>
                  <button onClick={() => handleEdit(item)} className="p-1.5 bg-white/10 hover:bg-white/20 rounded transition-colors">
                    <Edit size={14} className="text-[#00BFFF]" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-1.5 bg-white/10 hover:bg-red-500/20 rounded transition-colors">
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Reviews Management Tab
export const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ name: '', car: '', rating: 100, stars: 5, text: '' });

  const fetchReviews = async () => {
    try {
      const [res, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reviews`, { withCredentials: true }),
        axios.get(`${API}/admin/reviews/stats`, { withCredentials: true })
      ]);
      setReviews(res.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchReviews(); }, []);

  const resetForm = () => { setForm({ name: '', car: '', rating: 100, stars: 5, text: '' }); setEditingReview(null); setShowForm(false); };

  const handleEdit = (r) => {
    setForm({ name: r.name, car: r.car, rating: r.rating, stars: r.stars, text: r.text });
    setEditingReview(r); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.text) return;
    setSaving(true);
    try {
      if (editingReview) {
        await axios.put(`${API}/admin/reviews/${editingReview.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/reviews`, form, { withCredentials: true });
      }
      await fetchReviews(); resetForm();
    } catch (e) { alert(e.response?.data?.detail || 'Chyba'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Vymazať hodnotenie?')) return;
    await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
    await fetchReviews();
  };

  const toggleActive = async (r) => {
    await axios.put(`${API}/admin/reviews/${r.id}`, { active: !r.active }, { withCredentials: true });
    await fetchReviews();
  };

  if (showForm) {
    return (
      <div data-testid="review-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-medium text-white">{editingReview ? 'Upraviť hodnotenie' : 'Nové hodnotenie'}</h2>
          <button onClick={resetForm} className="text-zinc-400 hover:text-white flex items-center gap-2"><ArrowLeft size={18} /> Späť</button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Meno zákazníka</label>
              <input data-testid="review-form-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="Meno P." />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Auto</label>
              <input data-testid="review-form-car" value={form.car} onChange={(e) => setForm(p => ({ ...p, car: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="BMW E90" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Hodnotenie (%)</label>
              <input data-testid="review-form-rating" type="number" min="0" max="100" value={form.rating} onChange={(e) => setForm(p => ({ ...p, rating: parseInt(e.target.value) || 0 }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Hviezdičky (0-5)</label>
              <input data-testid="review-form-stars" type="number" min="0" max="5" step="0.5" value={form.stars} onChange={(e) => setForm(p => ({ ...p, stars: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Text hodnotenia</label>
            <textarea data-testid="review-form-text" value={form.text} onChange={(e) => setForm(p => ({ ...p, text: e.target.value }))}
              rows={4} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF] resize-y" placeholder="Text hodnotenia zákazníka..." />
          </div>
          <button data-testid="review-form-save" onClick={handleSave} disabled={saving || !form.name || !form.text}
            className="flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-3 font-semibold hover:bg-[#33CCFF] transition-all disabled:opacity-50">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {editingReview ? 'Uložiť' : 'Pridať hodnotenie'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reviews-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-medium text-white">Hodnotenia zákazníkov</h2>
          {stats && <p className="text-sm text-zinc-500 mt-1">{stats.active} aktívnych &bull; Priemer: {stats.avg_stars} hviezdičiek &bull; {stats.avg_rating}%</p>}
        </div>
        <button data-testid="review-add-btn" onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-4 py-2.5 font-semibold hover:bg-[#33CCFF] transition-all">
          <Plus size={18} /> Nové hodnotenie
        </button>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" /></div> : (
        <div className="space-y-3">
          {reviews.map(r => (
            <div key={r.id} data-testid={`review-item-${r.id}`}
              className={`flex items-center justify-between p-4 bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition-all ${!r.active ? 'opacity-40' : ''}`}>
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-white font-medium">{r.name}</h3>
                  <span className="text-zinc-500 text-sm">{r.car}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 ${r.rating === 100 ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{r.rating}%</span>
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} size={12} className={i <= r.stars ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'} />)}</div>
                </div>
                <p className="text-zinc-400 text-sm truncate">"{r.text}"</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleActive(r)} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-zinc-400">
                  {r.active ? <Eye size={16} className="text-green-400" /> : <EyeOff size={16} className="text-red-400" />}
                </button>
                <button onClick={() => handleEdit(r)} className="p-2 hover:bg-white/10 rounded-lg transition-colors text-zinc-400 hover:text-[#00BFFF]">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(r.id)} className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-zinc-400 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Blog Management Tab
export const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blogStats, setBlogStats] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', excerpt: '', content: '', category: 'general', 
    tags: '', published: true, featured: false
  });

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/stats`, { withCredentials: true });
      setBlogStats(response.data);
    } catch (error) {
      console.error('Error fetching blog stats:', error);
    }
  };

  useEffect(() => { fetchPosts(); fetchStats(); }, []);

  const generateSlug = (title) => {
    return title.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
  };

  const handleTitleChange = (val) => {
    setForm(prev => ({ ...prev, title: val, slug: editingPost ? prev.slug : generateSlug(val) }));
  };

  const resetForm = () => {
    setForm({ title: '', slug: '', excerpt: '', content: '', category: 'general', tags: '', published: true, featured: false });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setForm({
      title: post.title, slug: post.slug, excerpt: post.excerpt,
      content: post.content, category: post.category,
      tags: (post.tags || []).join(', '), published: post.published, featured: post.featured
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug || !form.content) return;
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
      }
      await fetchPosts();
      await fetchStats();
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Chyba pri ukladaní');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Naozaj chcete vymazať tento článok?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`, { withCredentials: true });
      await fetchPosts();
      await fetchStats();
    } catch (error) {
      alert('Chyba pri mazaní článku');
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { published: !post.published }, { withCredentials: true });
      await fetchPosts();
    } catch (error) {
      alert('Chyba pri zmene stavu');
    }
  };

  if (showForm) {
    return (
      <div data-testid="blog-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-medium text-white">
            {editingPost ? 'Upraviť článok' : 'Nový článok'}
          </h2>
          <button onClick={resetForm} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeft size={18} /> Späť
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Názov</label>
            <input data-testid="blog-form-title" value={form.title} onChange={(e) => handleTitleChange(e.target.value)}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="Názov článku" />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">URL slug</label>
            <input data-testid="blog-form-slug" value={form.slug} onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="url-slug" />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Krátky popis</label>
            <input data-testid="blog-form-excerpt" value={form.excerpt} onChange={(e) => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="Krátky popis článku" />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Obsah</label>
            <textarea data-testid="blog-form-content" value={form.content} onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              rows={12} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF] resize-y" placeholder="Obsah článku (podporuje **tučné** a [odkazy](url))" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Kategória</label>
              <select data-testid="blog-form-category" value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]">
                <option value="general">Všeobecné</option>
                <option value="bezpecnost">Bezpečnosť</option>
                <option value="technologie">Technológie</option>
                <option value="servis">Servis</option>
                <option value="tipy">Tipy</option>
                <option value="legislativa">Legislatíva</option>
                <option value="interiér">Interiér</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Tagy (oddelené čiarkou)</label>
              <input data-testid="blog-form-tags" value={form.tags} onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#00BFFF]" placeholder="tag1, tag2" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer text-zinc-400">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm(prev => ({ ...prev, published: e.target.checked }))}
                className="w-4 h-4 accent-[#00BFFF]" />
              Publikovaný
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-zinc-400">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm(prev => ({ ...prev, featured: e.target.checked }))}
                className="w-4 h-4 accent-[#00BFFF]" />
              Odporúčaný
            </label>
          </div>
          <button data-testid="blog-form-save" onClick={handleSave} disabled={saving || !form.title || !form.slug || !form.content}
            className="flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-6 py-3 font-semibold hover:bg-[#33CCFF] transition-all disabled:opacity-50">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {editingPost ? 'Uložiť zmeny' : 'Vytvoriť článok'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-medium text-white">Správa blogu</h2>
          {blogStats && (
            <p className="text-sm text-zinc-500 mt-1">
              {blogStats.total_posts} článkov &bull; {blogStats.published_posts} publikovaných &bull; {blogStats.total_views} zobrazení
            </p>
          )}
        </div>
        <button data-testid="blog-add-btn" onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-[#00BFFF] text-[#05050A] px-4 py-2.5 font-semibold hover:bg-[#33CCFF] transition-all">
          <Plus size={18} /> Nový článok
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
          <p>Žiadne články</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id} data-testid={`blog-item-${post.id}`}
              className="flex items-center justify-between p-4 bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] transition-all">
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-medium truncate">{post.title}</h3>
                  {post.featured && <Star size={14} className="text-yellow-400 flex-shrink-0" />}
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-500">
                  <span className="text-[#00BFFF] uppercase tracking-wider font-bold">{post.category}</span>
                  <span>{new Date(post.created_at).toLocaleDateString('sk-SK')}</span>
                  <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>
                  <span className={post.published ? 'text-green-400' : 'text-yellow-400'}>
                    {post.published ? 'Publikovaný' : 'Koncept'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => togglePublished(post)} title={post.published ? 'Skryť' : 'Publikovať'}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-zinc-400 hover:text-white">
                  {post.published ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button onClick={() => handleEdit(post)} data-testid={`blog-edit-${post.id}`}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-zinc-400 hover:text-[#00BFFF]">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(post.id)} data-testid={`blog-delete-${post.id}`}
                  className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-zinc-400 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
